/* eslint-disable no-param-reassign, no-shadow */
import { EditorStore, HISTORY_ACTION_MODE, Node } from '@candulabs/craft';
import fastDeepEqual from 'fast-deep-equal';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { BuiltInComponents } from 'craft/constants';
import { useEditor } from 'craft/hooks/useEditor';
import { useSchema } from 'craft/state/schema';
import { CustomNodeData, PropToEdit } from 'craft/types';
import { immerToES6 } from 'craft/utils';

import { getCommonNodeProperties, RetrievedCommonValues } from './getCommonNodeProperties';

type IncludedValues = {
  nodeIds: string[];
  type: string | BuiltInComponents.Multiple | undefined;
  props: Record<string, PropToEdit>;
};

type CollectedValues<C, D> = C extends null
  ? IncludedValues
  : RetrievedCommonValues<C, D> & IncludedValues;
export type useMultiNodesType<C = null, D = null> = CollectedValues<C, D> & {
  actions: {
    setProps: (
      props: Record<string, any>,
      historyMode?: HISTORY_ACTION_MODE,
      throttleNumber?: number,
    ) => void;
    setCustom: (
      custom: Record<string, any>,
      historyMode?: HISTORY_ACTION_MODE,
      throttleNumber?: number,
    ) => void;
    setCss: (
      css: Record<string, any>,
      historyMode?: HISTORY_ACTION_MODE,
      throttleNumber?: number,
    ) => void;
  };
};

export function useMultiNodes(nodeIds: string[]): useMultiNodesType;
export function useMultiNodes<C>(
  nodeIds: string[],
  collector: (node: Node | undefined, query: EditorStore['query']) => C,
): useMultiNodesType<C>;

export function useMultiNodes<C, D>(
  nodeIds: string[],
  collector: (node: Node | undefined, query: EditorStore['query']) => C,
  placeholderValue: D,
): useMultiNodesType<C, D>;

/**
 * useMultiNodes
 * @param nodeIds An array of nodeIds get common values from
 * @param collector A collector function to retrieve common values from the specified nodes
 * @param placeholderValue The value to set for when a collected property is different across the nodes (by default, this is null)
 */
export function useMultiNodes<C, D>(
  nodeIds: string[],
  collector?: any,
  placeholderValue: any = null,
): useMultiNodesType<C, D> {
  const schema = useSchema();
  const nodeIdsRef = useRef(nodeIds);
  const mountedRef = useRef(true);

  const { actions, query, store } = useEditor();

  const collectNodes = useCallback(() => {
    const { current: collectNodeIdsRef } = nodeIdsRef;
    const propTypes = getCommonNodeProperties(query, collectNodeIdsRef, (node) => {
      const propSchema =
        schema.components.find(({ type }) => type === node?.data.name)?.props ?? [];
      // eslint-disable-next-line @typescript-eslint/no-shadow
      return propSchema.reduce((accum: Record<string, any>, schema) => {
        accum[schema.id] = schema;
        return accum;
      }, {});
    });

    const { type, ...collected } = getCommonNodeProperties(
      query,
      collectNodeIdsRef,
      (node) => {
        let collectedValues = {};
        if (collector) {
          collectedValues = collector(node as Node, query);
        }
        return {
          type: node?.data.name,
          // TODO(prev): we should create a separate hook to get props across multiple nodes as it's a little complex
          props: Object.keys(propTypes).reduce((accum: Record<string, any>, key) => {
            // eslint-disable-next-line @typescript-eslint/no-shadow
            const schema = propTypes[key as keyof typeof propTypes];
            if (!schema) {
              return accum;
            }

            accum[key] = { schema, value: node?.data.props[key] };

            return accum;
          }, {}),
          ...collectedValues,
        };
      },
      placeholderValue,
    );

    let typeToReturn = type;
    if (collectNodeIdsRef.length === 0) {
      typeToReturn = undefined;
    } else if (typeToReturn === placeholderValue) {
      typeToReturn = BuiltInComponents.Multiple;
    }

    return {
      ...collected,
      props: collected.props || {},
      type: typeToReturn,
      nodeIds: collectNodeIdsRef,
    };
  }, []);

  const nodesRef = useRef<any>();

  // Get initial values on initial render
  if (!nodesRef.current || !fastDeepEqual(nodeIdsRef.current, nodeIds)) {
    nodeIdsRef.current = nodeIds;
    nodesRef.current = collectNodes();
  }

  // the only purpose of this set state is to trigger a refresh on subscribe
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [nodes, setNodes] = useState(nodesRef.current);

  // Collect states on state change
  const { subscribe } = store;
  useEffect(() => {
    const unsubscribe = subscribe(() => {
      const collectNewNodes = collectNodes();
      if (!fastDeepEqual(collectNewNodes, nodesRef.current) && mountedRef.current) {
        nodesRef.current = collectNewNodes;
        setNodes(collectNewNodes);
      }
    });

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
      mountedRef.current = false;
    };
  }, []);

  const multiNodesAction = useMemo(
    () => ({
      setProps: immerToES6((callback: (data: any) => void, historyMode, throttleNumber = 0) => {
        if (throttleNumber) {
          return actions.setProp(nodesRef.current.nodeIds, callback, historyMode, throttleNumber);
        }

        return actions.setProp(nodesRef.current.nodeIds, callback);
      }),
      setCss: immerToES6((callback, historyMode, throttleNumber = 0) => {
        return actions.setCustom(
          nodesRef.current.nodeIds,
          (custom: CustomNodeData) => callback(custom.css),
          historyMode,
          throttleNumber,
        );
      }),
      setCustom: immerToES6((callback: (data: any) => void, historyMode, throttleNumber = 0) => {
        return actions.setCustom(nodesRef.current.nodeIds, callback, historyMode, throttleNumber);
      }),
    }),
    [],
  );

  return {
    ...nodesRef.current,
    actions: multiNodesAction,
  };
}

export default useMultiNodes;
